import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import {
  type ShopBanner,
  type ShopBannerInput,
  listBanners,
  createBanner,
  updateBanner,
  deleteBanner,
} from '@/services/shopAdminApi';
import {
  Plus, Trash2, Save, X, AlertCircle, CheckCircle2, Loader2,
  Megaphone, LayoutPanelTop, ShoppingCart, ArrowRight,
} from 'lucide-react';

const POSITIONS = [
  { value: 'header', label: 'Header bar', icon: Megaphone },
  { value: 'hero', label: 'Hero section', icon: LayoutPanelTop },
  { value: 'checkout', label: 'Checkout page', icon: ShoppingCart },
  { value: 'footer', label: 'Footer', icon: ArrowRight },
];

const EMPTY_BANNER: ShopBannerInput = {
  title: '',
  subtitle: '',
  link_url: '',
  background_color: '#567D38',
  text_color: '#FFFFFF',
  position: 'header',
  priority: 0,
  is_active: true,
  starts_at: null,
  ends_at: null,
};

export function BannersTab() {
  const [banners, setBanners] = useState<ShopBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ShopBannerInput>(EMPTY_BANNER);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await listBanners();
      setBanners(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load banners');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startCreate() {
    setEditingId('new');
    setForm(EMPTY_BANNER);
  }

  function startEdit(b: ShopBanner) {
    setEditingId(b.id);
    setForm({
      title: b.title,
      subtitle: b.subtitle || '',
      link_url: b.link_url || '',
      background_color: b.background_color,
      text_color: b.text_color,
      position: b.position,
      priority: b.priority,
      is_active: b.is_active,
      starts_at: b.starts_at,
      ends_at: b.ends_at,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_BANNER);
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      if (editingId === 'new') {
        await createBanner(form);
        setMessage({ type: 'success', text: 'Banner created.' });
      } else if (editingId) {
        await updateBanner(editingId, form);
        setMessage({ type: 'success', text: 'Banner updated.' });
      }
      setEditingId(null);
      setForm(EMPTY_BANNER);
      await load();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this banner?')) return;
    try {
      await deleteBanner(id);
      await load();
      setMessage({ type: 'success', text: 'Banner deleted.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Delete failed.' });
    }
  }

  function update<K extends keyof ShopBannerInput>(key: K, value: ShopBannerInput[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  if (loading) return <div className="p-8 text-center text-[#6B7B6E]">Loading banners…</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1A2E12]">Banners</h2>
        <button
          onClick={startCreate}
          disabled={editingId === 'new'}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#BDD630] text-[#1A2E12] rounded-lg text-sm font-semibold hover:bg-[#96c23d] disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Add Banner
        </button>
      </div>

      {message && (
        <div className={cn(
          'flex items-start gap-2 rounded-lg px-4 py-3 text-sm',
          message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
        )}>
          {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
          {message.text}
        </div>
      )}

      {editingId === 'new' && (
        <BannerForm
          form={form}
          update={update}
          saving={saving}
          onSave={handleSave}
          onCancel={cancelEdit}
        />
      )}

      <div className="space-y-3">
        {banners.length === 0 && <p className="text-sm text-[#6B7B6E]">No banners yet.</p>}
        {banners.map(b => (
          <div key={b.id} className="bg-white rounded-xl border border-[#D9B38C]/20 overflow-hidden">
            {editingId === b.id ? (
              <BannerForm
                form={form}
                update={update}
                saving={saving}
                onSave={handleSave}
                onCancel={cancelEdit}
              />
            ) : (
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div
                      className="rounded-lg px-4 py-3 mb-3"
                      style={{ backgroundColor: b.background_color, color: b.text_color }}
                    >
                      <p className="font-semibold">{b.title}</p>
                      {b.subtitle && <p className="text-sm opacity-90">{b.subtitle}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-[#6B7B6E]">
                      <span className="px-2 py-1 bg-[#F7F5EE] rounded">{b.position}</span>
                      <span className="px-2 py-1 bg-[#F7F5EE] rounded">priority {b.priority}</span>
                      <span className={cn('px-2 py-1 rounded', b.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600')}>
                        {b.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {b.starts_at && <span className="px-2 py-1 bg-[#F7F5EE] rounded">from {b.starts_at.slice(0, 10)}</span>}
                      {b.ends_at && <span className="px-2 py-1 bg-[#F7F5EE] rounded">until {b.ends_at.slice(0, 10)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(b)} className="p-2 text-[#6B7B6E] hover:text-[#1A2E12] hover:bg-[#F7F5EE] rounded-lg">
                      <Save className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(b.id)} className="p-2 text-[#6B7B6E] hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BannerForm({
  form,
  update,
  saving,
  onSave,
  onCancel,
}: {
  form: ShopBannerInput;
  update: <K extends keyof ShopBannerInput>(key: K, value: ShopBannerInput[K]) => void;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="bg-[#F7F5EE] rounded-xl p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Title" value={form.title} onChange={v => update('title', v)} />
        <Input label="Subtitle" value={form.subtitle || ''} onChange={v => update('subtitle', v)} />
        <Input label="Link URL" value={form.link_url || ''} onChange={v => update('link_url', v)} />
        <Select label="Position" value={form.position} onChange={v => update('position', v)} options={POSITIONS.map(p => ({ value: p.value, label: p.label }))} />
        <ColorInput label="Background" value={form.background_color} onChange={v => update('background_color', v)} />
        <ColorInput label="Text color" value={form.text_color} onChange={v => update('text_color', v)} />
        <Input label="Priority" type="number" value={String(form.priority)} onChange={v => update('priority', parseInt(v || '0', 10))} />
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-[#1A2E12]">
            <input type="checkbox" checked={form.is_active} onChange={e => update('is_active', e.target.checked)} />
            Active
          </label>
        </div>
        <Input label="Starts at" type="datetime-local" value={form.starts_at ? form.starts_at.slice(0, 16) : ''} onChange={v => update('starts_at', v ? new Date(v).toISOString() : null)} />
        <Input label="Ends at" type="datetime-local" value={form.ends_at ? form.ends_at.slice(0, 16) : ''} onChange={v => update('ends_at', v ? new Date(v).toISOString() : null)} />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-[#6B7B6E] hover:bg-white rounded-lg"><X className="w-4 h-4 inline mr-1" /> Cancel</button>
        <button onClick={onSave} disabled={saving} className="px-4 py-2 bg-[#BDD630] text-[#1A2E12] rounded-lg text-sm font-semibold hover:bg-[#96c23d] disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <Save className="w-4 h-4 inline mr-1" />}
          Save
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-[#6B7B6E]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#BDD630]"
      />
    </div>
  );
}

function ColorInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-[#6B7B6E]">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-10 h-9 p-1 rounded border" />
        <input type="text" value={value} onChange={e => onChange(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm" />
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-[#6B7B6E]">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#BDD630] bg-white"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
